<script lang="ts">
import {
  computed,
  defineComponent,
  inject,
  onActivated,
  onDeactivated,
  onUpdated,
  PropType,
  reactive,
  ref,
  watch,
} from "vue";
import Steps from "@/components/Steps.vue";
import { NavvItem } from "@/model/NavItem";
import VorschaedenFHVGV from "@/components/vorschaeden/VorschaedenFHVGV.vue";
import VorschaedenFHVHV from "@/components/vorschaeden/VorschaedenFHVHV.vue";
import VorschaedenFHHUG from "@/components/vorschaeden/VorschaedenFHHUG.vue";
import Sanierungsfragen from "@/components/sanierung/Sanierungsfragen.vue";
import formatUtils from "@/businesslogic/utils/formatUtils";
import formValidierung from "@/businesslogic/formValidierung";
import Logger from "js-logger";
import AnfrageService from "@/businesslogic/services/AnfrageService";
import VorversicherungFH from "@/components/vorversicherung/VorversicherungFH.vue";
import Zeile from "@/components/grid/Zeile.vue";
import Zelle from "@/components/grid/Zelle.vue";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import Modal from "@/components/Modal.vue";
import storeUtils from "@/businesslogic/utils/storeUtils";
import sanierungUtils from "@/businesslogic/utils/sanierungUtils";
import ExtraSelbstbeteiligungService from "@/businesslogic/services/ExtraSelbstbeteiligungService";
import dateUtils from "@/businesslogic/utils/dateUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "HistorieFH2024",
  // eslint-disable-next-line no-undef
  components: {
    Steps,
    Ueberschrift,
    Zelle,
    Zeile,
    VorversicherungFH,
    VorschaedenFHVGV,
    VorschaedenFHVHV,
    VorschaedenFHHUG,
    Sanierungsfragen,
    Modal,
  },
  props: { navItems: Object as PropType<NavvItem[]> },
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  setup: function (_props, { emit }) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const zeigeSpinner: any = inject("zeigeSpinner");
    const aelterAls30 = computed(() => {
      return gebaeudeAelter30(
        dateStringToIsoDate(piniaStore.state.ferienhaus.vgv.tarifinformationen.versicherungsbeginn),
      );
    });
    const aelterAls50 = computed(() => {
      return gebaeudeAelter50(
        dateStringToIsoDate(piniaStore.state.ferienhaus.vgv.tarifinformationen.versicherungsbeginn),
      );
    });
    const { formatCurrency } = formatUtils();
    const { dateStringToIsoDate } = dateUtils();
    const { pruefeSanierung, gebaeudeAelter30, gebaeudeAelter50 } = sanierungUtils();
    const fehler = ref({} as any);
    let komponenteIstAktiv = false;
    const modalBeitragsaenderung = reactive({
      titel: "Beitragsänderung",
      text: "Durch Ihre Änderung wurde der Beitrag angepasst. Bitte überprüfen Sie den neuen Beitrag.",
      hatSchliessenButton: true,
      schliessenButtonText: "OK",
    });
    const pruefeHalbjaehrlichFH = computed(() => {
      for (let i = 0; i <= piniaStore.state.preise.produktLinien.length - 1; i++) {
        if (piniaStore.state.preise.produktLinien[i].halbjaehrlich < 25.0) {
          return false;
        }
      }
      return true;
    });
    const pruefeVierteljaehrlichFH = computed(() => {
      for (let i = 0; i <= piniaStore.state.preise.produktLinien.length - 1; i++) {
        if (piniaStore.state.preise.produktLinien[i].vierteljaehrlich < 25.0) {
          return false;
        }
      }
      return true;
    });
    const pruefeMonatlichFH = computed(() => {
      for (let i = 0; i <= piniaStore.state.preise.produktLinien.length - 1; i++) {
        if (piniaStore.state.preise.produktLinien[i].monatlich < 25.0) {
          return false;
        }
      }
      return true;
    });

    const flachdach = computed(() => {
      return piniaStore.state.gebaeudedaten.dachvariante === "1";
    });

    const vgvsichtbar = ref(false);
    const vhvsichtbar = ref(false);
    const hugsichtbar = ref(false);
    let beitragsaenderungDurchSanierung = false;
    const istAktiv = ref(false);
    const { enthaeltVGV } = storeUtils();
    const checkSparten = () => {
      switch (piniaStore.state.ferienhaus.versicherungsumfang) {
        case "0001":
          {
            vgvsichtbar.value = true;
            vhvsichtbar.value = true;
            if (piniaStore.state.tarifinformationen.hausundgrundbesitzerhaftpflicht) {
              hugsichtbar.value = true;
            }
          }
          break;
        case "0002":
          {
            vgvsichtbar.value = true;
            vhvsichtbar.value = false;
            if (piniaStore.state.tarifinformationen.hausundgrundbesitzerhaftpflicht) {
              hugsichtbar.value = true;
            }
          }
          break;
        case "0003":
          {
            vgvsichtbar.value = false;
            vhvsichtbar.value = true;
            hugsichtbar.value = false;
          }
          break;
      }
    };

    const anfragePruefenUndWeiter = () => {
      piniaStore.state.neuberechnen = true;

      AnfrageService.pruefen(piniaStore.getSessionToken(piniaStore.state), piniaStore.state, "HistorieFH2024")
        .then((res) => {
          Logger.debug("anfragePruefenUndWeiter(): OK. Ergebnis: ", res.data);
          if (res.data && res.data.aussteuerung !== undefined) {
            piniaStore.state.isDunkelverarbeitung = res.data.aussteuerung === false;
          }
          piniaStore.aktualisiereAussteuerungen(res.data);
        })
        .catch((err) => {
          Logger.debug("anfragePruefenUndWeiter(): Fehler:", err);
          zeigeSpinner.value = false;
        });
    };

    const eingabenSindValide = () => {
      return new Promise((resolve) => {
        const { validiereForm, getFormFehlerAnzahl, getFormFehler } = formValidierung();
        validiereForm("").then(() => {
          fehler.value = getFormFehler();
          resolve(getFormFehlerAnzahl() === 0);
        });
      });
    };

    const nextPage = () => {
      Logger.debug("next-page", eingabenSindValide());
      eingabenSindValide().then((isValid) => {
        if (isValid) {
          anfragePruefenUndWeiter();
        }
      });
    };

    watch(
      () => piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinien.length - 1].gemZahlungsweise,
      (beitragNeu, beitragAlt) => {
        Logger.debug("WATCH (Historie>zahlbeitrag):\nbeitragNeu:" + beitragNeu + "\nbeitragAlt:" + beitragAlt);
        if (komponenteIstAktiv && beitragAlt > 0 && beitragNeu !== beitragAlt) {
          Logger.debug("WATCH (Historie>zahlbeitrag): Beitrag hat sich geändert.");
          modalBeitragsaenderung.titel = "Beitragsänderung";
          modalBeitragsaenderung.text =
            "Durch Ihre Änderung ändert sich Ihr Beitrag von " +
            formatCurrency(beitragAlt) +
            " auf " +
            formatCurrency(beitragNeu);
          Logger.debug(modalBeitragsaenderung.text);
          wechsleSichtbarkeit("modalBeitragsaenderung");
        }
        if (pruefeMonatlichFH.value && pruefeVierteljaehrlichFH.value && pruefeHalbjaehrlichFH.value) {
          piniaStore.state.tarifinformationen.anpassungZahlungsweise = false;
        }
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.kernsanierung,
      (wertNeu, wertAlt) => {
        if (wertNeu !== wertAlt) {
          piniaStore.state.ferienhaus.vgv.tarifinformationen.kernsanierung =
            piniaStore.state.tarifinformationen.kernsanierung;
          beitragsaenderungDurchSanierung = true;
        }
      },
    );
    watch(
      () => piniaStore.state.tarifinformationen.extraSelbstbeteiligungLeitungswasser,
      (wertNeu, wertAlt) => {
        if (wertNeu !== wertAlt) {
          piniaStore.state.ferienhaus.vgv.tarifinformationen.extraSelbstbeteiligungLeitungswasser =
            piniaStore.state.tarifinformationen.extraSelbstbeteiligungLeitungswasser;
        }
      },
    );

    watch(
      () => piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinien.length - 1],
      (preiseNeu, preiseAlt) => {
        Logger.debug(
          "WATCH (Historie>zahlbeitrag):\nbeitragNeu:" +
            preiseNeu.gemZahlungsweise +
            "\nbeitragAlt:" +
            preiseAlt.gemZahlungsweise,
        );
        if (
          preiseAlt.gemZahlungsweise > 0 &&
          preiseNeu.gemZahlungsweise !== preiseAlt.gemZahlungsweise &&
          beitragsaenderungDurchSanierung
        ) {
          piniaStore.state.ferienhaus.vgv.tarifinformationen.beitragVorKernsanierungGemZahlungsweise =
            preiseAlt.gemZahlungsweise;
          piniaStore.state.ferienhaus.vgv.tarifinformationen.beitragVorKernsanierungJaehrlich = preiseAlt.jaehrlich;
          beitragsaenderungDurchSanierung = false;

          for (let i = 0; i <= piniaStore.state.preise.produktLinien.length - 1; i++) {
            if (piniaStore.state.preise.produktLinien[i].gemZahlungsweise < 25.0) {
              piniaStore.state.tarifinformationen.anpassungZahlungsweise = true;
            }
          }
        }
      },
    );

    watch(
      () => [piniaStore.state.berechnungDurchgefuehrt],
      () => {
        if (piniaStore.state.berechnungDurchgefuehrt && istAktiv.value) {
          piniaStore.state.berechnungDurchgefuehrt = false;
          emit("next-page", { pageIndex: 4 });
        }
      },
    );

    function pruefeZusaetzlicheSelbstbeteiligung() {
      piniaStore.state.ferienhaus.vgv.tarifinformationen.extraSelbstbeteiligung = 0;
      if (
        enthaeltVGV(piniaStore.state) &&
        ExtraSelbstbeteiligungService.vgvSelbstbeteiligungVorversicherungFehltPruefen(
          piniaStore.state.isMakler,
          piniaStore.state.ferienhaus.vgv.vorversicherung.vertragEnde,
          piniaStore.state.ferienhaus.vgv.vorversicherung.vorhanden,
          piniaStore.state.ferienhaus.vgv.tarifinformationen.versicherungsbeginn,
        )
      ) {
        piniaStore.state.ferienhaus.vgv.tarifinformationen.extraSelbstbeteiligung = 1000;
      }
    }

    onUpdated(() => {
      checkSparten();
    });

    onActivated(() => {
      komponenteIstAktiv = true;
      checkSparten();

      istAktiv.value = true;
      piniaStore.state.berechnungDurchgefuehrt = false;
      pruefeSanierung(piniaStore.state.ferienhaus.vgv.tarifinformationen.versicherungsbeginn);
    });

    onDeactivated(() => {
      komponenteIstAktiv = false;
      istAktiv.value = false;
      pruefeSanierung(piniaStore.state.ferienhaus.vgv.tarifinformationen.versicherungsbeginn);
      pruefeZusaetzlicheSelbstbeteiligung();
    });
    return {
      piniaStore,
      istSichtbar,
      wechsleSichtbarkeit,
      zeigeSpinner,
      vgvsichtbar,
      vhvsichtbar,
      hugsichtbar,
      formatCurrency,
      modalBeitragsaenderung,
      fehler,
      aelterAls30,
      aelterAls50,
      nextPage,
      enthaeltVGV,
      flachdach,
    };
  },
});
</script>

<template>
  <steps :active="4" v-bind:items="navItems" @goto-page="$emit('goto-page', $event)" />
  <section>
    <ueberschrift titel="Angaben zu Vorversicherungen" />
    <vorversicherungFH v-if="vgvsichtbar" :fehler="fehler" produkt="vgv" />
    <vorversicherungFH v-if="vhvsichtbar" :fehler="fehler" produkt="vhv" />
    <vorversicherungFH v-if="hugsichtbar" :fehler="fehler" produkt="hug" />

    <ueberschrift titel="Angaben zu Vorschäden" />
    <vorschaedenFHVGV v-if="vgvsichtbar" :fehler="fehler" produkt="Wohngebäude" />
    <vorschaedenFHVHV v-if="vhvsichtbar" :fehler="fehler" produkt="Hausrat" />
    <vorschaedenFHHUG v-if="hugsichtbar" :fehler="fehler" produkt="Haus- und Grundbesitzerhaftpflicht" />
    <sanierungsfragen
      v-if="aelterAls30 && enthaeltVGV(piniaStore.state) && (aelterAls50 || flachdach)"
      :aelterAls50="aelterAls50"
      :fehler="fehler"
    ></sanierungsfragen>

    <!-- Absenden -->
    <zeile>
      <zelle :offset="4" :breite="6">
        <button
          class="c-button c-button--full-width u-mar-t-2 u-mar-b-2 mobil-mehrAbstand"
          @click="nextPage()"
          data-cy="submitHistorieDetails"
        >
          Zusammenfassung und Abschluss
        </button>
      </zelle>
    </zeile>
  </section>
  <Modal
    v-if="istSichtbar('modalBeitragsaenderung')"
    v-bind="modalBeitragsaenderung"
    @closeModal="wechsleSichtbarkeit('modalBeitragsaenderung')"
  />
</template>
